import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, Plus, Trash2, RefreshCw, Eye, EyeOff,
  Save, X, Loader2, GripVertical, Video, Image as ImageIcon, RotateCcw
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { adminFetch } from '@/utils/adminAuth';

const CATEGORIES = [
  { id: 'parades', label: 'Parades & Meet-and-Greet' },
  { id: 'samba', label: 'Samba Shows' },
  { id: 'cultural', label: 'Afro-Brasilian Cultural' },
  { id: 'schools', label: 'Schools & Kids' },
  { id: 'teambuilding', label: 'Team Building & Fitness' },
  { id: 'costumes', label: 'Costumes & Photo Ops' },
];

const EMPTY_PERF = {
  number: 99,
  title: '',
  category: 'parades',
  image: '',
  description: '',
  videoLinks: [],
  timeSlots: '',
  recommendations: '',
  active: true,
  showBookNow: true,
};

export const AdminPerformances = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null); // performance being edited, or null
  const [isNew, setIsNew] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminFetch('/admin/performances');
      const data = await res.json();
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error('Failed to load performances');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openEdit = (perf) => { setIsNew(false); setEditing({ ...perf }); };
  const openNew = () => {
    const nextNum = items.length ? Math.max(...items.map(i => i.number)) + 1 : 1;
    setIsNew(true);
    setEditing({ ...EMPTY_PERF, number: nextNum });
  };
  const closeEdit = () => setEditing(null);

  const save = async () => {
    if (!editing.title.trim()) return toast.error('Title is required');
    if (!editing.image.trim()) return toast.error('Image URL is required');
    setSaving(true);
    try {
      const payload = { ...editing };
      // strip client-only fields
      delete payload.created_at;
      delete payload.updated_at;
      let res;
      if (isNew) {
        res = await adminFetch('/admin/performances', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      } else {
        const { id, ...body } = payload;
        res = await adminFetch(`/admin/performances/${id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
      }
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Save failed');
      }
      toast.success(isNew ? 'Performance created' : 'Performance updated');
      closeEdit();
      await load();
    } catch (e) {
      toast.error(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (perf) => {
    if (!window.confirm(`Delete "${perf.title}"? This cannot be undone.`)) return;
    try {
      const res = await adminFetch(`/admin/performances/${perf.id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Delete failed');
      toast.success('Deleted');
      await load();
    } catch (e) {
      toast.error(e.message || 'Delete failed');
    }
  };

  const toggleActive = async (perf) => {
    try {
      const res = await adminFetch(`/admin/performances/${perf.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ active: !perf.active }),
      });
      if (!res.ok) throw new Error('Update failed');
      await load();
    } catch (e) {
      toast.error(e.message || 'Update failed');
    }
  };

  const resetDefaults = async () => {
    if (!window.confirm('This will DELETE all current performances and restore the 17 originals. Proceed?')) return;
    try {
      const res = await adminFetch('/admin/performances/reset-defaults', { method: 'POST' });
      if (!res.ok) throw new Error('Reset failed');
      toast.success('Restored to defaults');
      await load();
    } catch (e) {
      toast.error(e.message || 'Reset failed');
    }
  };

  const dedupeDuplicates = async () => {
    try {
      const res = await adminFetch('/admin/performances/dedupe', { method: 'POST' });
      if (!res.ok) throw new Error('Dedupe failed');
      const data = await res.json();
      if (data.removed > 0) {
        toast.success(`Removed ${data.removed} duplicate${data.removed === 1 ? '' : 's'} (${data.before} → ${data.after})`);
      } else {
        toast.success('No duplicates found - all clean!');
      }
      await load();
    } catch (e) {
      toast.error(e.message || 'Dedupe failed');
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="max-w-6xl mx-auto py-10 px-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
          <div className="flex items-center gap-4">
            <Link to="/admin/dashboard" className="text-muted-foreground hover:text-primary transition-colors" data-testid="back-to-admin">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-3xl font-black">Performance Details Editor</h1>
              <p className="text-sm text-muted-foreground">Edit titles, descriptions, images and video links shown on the public Performances page.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={load}
              className="px-3 py-2 rounded-lg bg-card border border-border hover:border-primary/50 text-sm font-semibold flex items-center gap-2"
              data-testid="reload-perfs"
            >
              <RefreshCw className="w-4 h-4" />
              Reload
            </button>
            <button
              onClick={dedupeDuplicates}
              className="px-3 py-2 rounded-lg bg-card border border-border hover:border-emerald-500/60 text-sm font-semibold flex items-center gap-2 text-emerald-400"
              data-testid="dedupe-perfs"
              title="Scan and remove duplicate performances (same number + title)"
            >
              <Trash2 className="w-4 h-4" />
              Clean Duplicates
            </button>
            <button
              onClick={resetDefaults}
              className="px-3 py-2 rounded-lg bg-card border border-border hover:border-yellow-500/60 text-sm font-semibold flex items-center gap-2 text-yellow-400"
              data-testid="reset-defaults"
            >
              <RotateCcw className="w-4 h-4" />
              Reset to defaults
            </button>
            <button
              onClick={openNew}
              className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-bold flex items-center gap-2 hover:opacity-90"
              data-testid="add-performance-btn"
            >
              <Plus className="w-4 h-4" />
              New Performance
            </button>
          </div>
        </div>

        {/* Duplicate detection banner */}
        {(() => {
          const seen = new Map();
          const dupeGroups = [];
          for (const p of items) {
            const k = `${p.number}|${(p.title || '').trim().toLowerCase()}|${(p.category || '').trim().toLowerCase()}`;
            if (seen.has(k)) {
              const g = seen.get(k);
              g.count += 1;
              g.titles.add(p.title);
            } else {
              seen.set(k, { count: 1, titles: new Set([p.title]) });
            }
          }
          const dupeCount = Array.from(seen.values()).reduce((n, g) => n + (g.count > 1 ? g.count - 1 : 0), 0);
          if (dupeCount === 0) return null;
          return (
            <div
              className="mb-6 p-4 rounded-xl border border-yellow-500/60 bg-yellow-500/10 flex items-center justify-between gap-3 flex-wrap"
              data-testid="dedupe-banner"
            >
              <div className="text-sm text-yellow-100">
                <strong>{dupeCount} duplicate performance{dupeCount === 1 ? '' : 's'} detected.</strong>{' '}
                Visitors are seeing the same cards twice on the public page.
              </div>
              <button
                onClick={dedupeDuplicates}
                className="px-4 py-2 rounded-lg bg-yellow-500 text-black text-sm font-bold hover:bg-yellow-400 flex items-center gap-2"
                data-testid="dedupe-banner-btn"
              >
                <Trash2 className="w-4 h-4" />
                Clean {dupeCount} Duplicate{dupeCount === 1 ? '' : 's'}
              </button>
            </div>
          );
        })()}

        {/* List */}
        {loading ? (
          <div className="flex items-center gap-2 text-muted-foreground py-16 justify-center">
            <Loader2 className="w-5 h-5 animate-spin" />
            Loading...
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            No performances yet.
            <button onClick={resetDefaults} className="ml-2 text-primary underline">
              Restore defaults
            </button>
          </div>
        ) : (
          <div className="grid gap-3">
            {[...items].sort((a, b) => a.number - b.number).map((perf) => (
              <div
                key={perf.id}
                className={`flex items-center gap-4 p-3 rounded-xl border ${
                  perf.active ? 'bg-card border-border' : 'bg-card/40 border-border/50 opacity-60'
                }`}
                data-testid={`perf-row-${perf.number}`}
              >
                <div className="text-muted-foreground w-6 text-center">
                  <GripVertical className="w-4 h-4 inline-block" />
                </div>
                <div className="w-10 h-10 rounded-full bg-primary/20 text-primary font-bold text-sm flex items-center justify-center flex-shrink-0">
                  {perf.number}
                </div>
                <div className="w-16 h-16 rounded-lg overflow-hidden bg-black/40 flex-shrink-0 relative">
                  {perf.image ? (
                    <img loading="lazy" decoding="async" src={perf.image} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <ImageIcon className="w-6 h-6 text-muted-foreground m-auto absolute inset-0" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-bold truncate">{perf.title}</h3>
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-primary/15 text-primary border border-primary/30">
                      {CATEGORIES.find(c => c.id === perf.category)?.label || perf.category}
                    </span>
                    {perf.videoLinks?.length > 0 && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-muted-foreground">
                        <Video className="w-3 h-3" />
                        {perf.videoLinks.length}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground line-clamp-1 mt-1">
                    {perf.description}
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => toggleActive(perf)}
                    className="p-2 rounded-lg hover:bg-white/5"
                    title={perf.active ? 'Hide from site' : 'Show on site'}
                    data-testid={`toggle-active-${perf.number}`}
                  >
                    {perf.active ? <Eye className="w-4 h-4 text-green-400" /> : <EyeOff className="w-4 h-4 text-muted-foreground" />}
                  </button>
                  <button
                    onClick={() => openEdit(perf)}
                    className="px-3 py-1.5 rounded-lg bg-primary/15 text-primary text-xs font-bold hover:bg-primary/25"
                    data-testid={`edit-perf-${perf.number}`}
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => remove(perf)}
                    className="p-2 rounded-lg hover:bg-red-500/15 text-red-400"
                    title="Delete"
                    data-testid={`delete-perf-${perf.number}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Edit Modal */}
      <AnimatePresence>
        {editing && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto"
            onClick={closeEdit}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.15 }}
              onClick={e => e.stopPropagation()}
              className="my-8 w-full max-w-3xl bg-background border border-border rounded-2xl shadow-2xl"
              data-testid="edit-modal"
            >
              {/* Header */}
              <div className="flex items-center justify-between p-5 border-b border-border">
                <h2 className="text-xl font-black">
                  {isNew ? 'New Performance' : `Editing #${editing.number}`}
                </h2>
                <button onClick={closeEdit} className="p-2 rounded-lg hover:bg-white/5">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Body */}
              <div className="p-5 space-y-4 max-h-[calc(100vh-200px)] overflow-y-auto">
                {/* Number + Category */}
                <div className="grid grid-cols-2 gap-3">
                  <label className="block">
                    <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Order # (1 = first)</span>
                    <input
                      type="number"
                      value={editing.number}
                      onChange={e => setEditing({ ...editing, number: Number(e.target.value) })}
                      className="mt-1 w-full px-3 py-2 rounded-lg bg-card border border-border text-sm"
                      data-testid="edit-number"
                    />
                  </label>
                  <label className="block">
                    <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Category</span>
                    <select
                      value={editing.category}
                      onChange={e => setEditing({ ...editing, category: e.target.value })}
                      className="mt-1 w-full px-3 py-2 rounded-lg bg-card border border-border text-sm"
                      data-testid="edit-category"
                    >
                      {CATEGORIES.map(c => (
                        <option key={c.id} value={c.id}>{c.label}</option>
                      ))}
                    </select>
                  </label>
                </div>

                {/* Title */}
                <label className="block">
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Title</span>
                  <input
                    type="text"
                    value={editing.title}
                    onChange={e => setEditing({ ...editing, title: e.target.value })}
                    className="mt-1 w-full px-3 py-2 rounded-lg bg-card border border-border text-sm"
                    data-testid="edit-title"
                  />
                </label>

                {/* Image URL + Preview */}
                <label className="block">
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Image URL</span>
                  <input
                    type="url"
                    value={editing.image}
                    onChange={e => setEditing({ ...editing, image: e.target.value })}
                    className="mt-1 w-full px-3 py-2 rounded-lg bg-card border border-border text-sm"
                    placeholder="https://..."
                    data-testid="edit-image"
                  />
                  {editing.image ? (
                    <div className="mt-2 aspect-video rounded-lg overflow-hidden bg-black/40 max-w-sm">
                      <img loading="lazy" decoding="async" src={editing.image} alt="preview" className="w-full h-full object-cover" />
                    </div>
                  ) : null}
                </label>

                {/* Description */}
                <label className="block">
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Description</span>
                  <textarea
                    value={editing.description}
                    onChange={e => setEditing({ ...editing, description: e.target.value })}
                    rows={6}
                    className="mt-1 w-full px-3 py-2 rounded-lg bg-card border border-border text-sm font-mono"
                    data-testid="edit-description"
                  />
                </label>

                {/* Video Links (repeater) */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Video Links</span>
                    <button
                      onClick={() => setEditing({
                        ...editing,
                        videoLinks: [...(editing.videoLinks || []), { label: '', url: '' }],
                      })}
                      className="text-xs font-bold text-primary hover:underline flex items-center gap-1"
                      data-testid="add-video-link"
                    >
                      <Plus className="w-3 h-3" />
                      Add link
                    </button>
                  </div>
                  <div className="space-y-2">
                    {(editing.videoLinks || []).map((v, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <input
                          type="text"
                          value={v.label}
                          onChange={e => {
                            const next = [...editing.videoLinks];
                            next[i] = { ...next[i], label: e.target.value };
                            setEditing({ ...editing, videoLinks: next });
                          }}
                          placeholder="Label (e.g. Watch Video)"
                          className="flex-1 px-3 py-2 rounded-lg bg-card border border-border text-sm"
                          data-testid={`video-label-${i}`}
                        />
                        <input
                          type="url"
                          value={v.url}
                          onChange={e => {
                            const next = [...editing.videoLinks];
                            next[i] = { ...next[i], url: e.target.value };
                            setEditing({ ...editing, videoLinks: next });
                          }}
                          placeholder="https://vimeo.com/..."
                          className="flex-[2] px-3 py-2 rounded-lg bg-card border border-border text-sm"
                          data-testid={`video-url-${i}`}
                        />
                        <button
                          onClick={() => setEditing({
                            ...editing,
                            videoLinks: editing.videoLinks.filter((_, x) => x !== i),
                          })}
                          className="p-2 rounded-lg hover:bg-red-500/15 text-red-400"
                          title="Remove"
                          data-testid={`remove-video-${i}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    {(!editing.videoLinks || editing.videoLinks.length === 0) && (
                      <p className="text-xs text-muted-foreground italic">No videos yet. First Vimeo link here becomes the click-to-play preview on the public card.</p>
                    )}
                  </div>
                </div>

                {/* Time slots + recommendations */}
                <label className="block">
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Time Slots</span>
                  <input
                    type="text"
                    value={editing.timeSlots || ''}
                    onChange={e => setEditing({ ...editing, timeSlots: e.target.value })}
                    className="mt-1 w-full px-3 py-2 rounded-lg bg-card border border-border text-sm"
                    data-testid="edit-timeslots"
                  />
                </label>
                <label className="block">
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Recommended for</span>
                  <input
                    type="text"
                    value={editing.recommendations || ''}
                    onChange={e => setEditing({ ...editing, recommendations: e.target.value })}
                    className="mt-1 w-full px-3 py-2 rounded-lg bg-card border border-border text-sm"
                    data-testid="edit-recommendations"
                  />
                </label>

                {/* Toggles */}
                <div className="flex flex-wrap gap-4 pt-2">
                  <label className="inline-flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={!!editing.active}
                      onChange={e => setEditing({ ...editing, active: e.target.checked })}
                      data-testid="edit-active"
                    />
                    Visible on public site
                  </label>
                  <label className="inline-flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={!!editing.showBookNow}
                      onChange={e => setEditing({ ...editing, showBookNow: e.target.checked })}
                      data-testid="edit-book-now"
                    />
                    Show &quot;Book This Performance&quot; button
                  </label>
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end gap-3 p-5 border-t border-border">
                <button
                  onClick={closeEdit}
                  className="px-4 py-2 rounded-lg bg-card border border-border text-sm font-semibold"
                >
                  Cancel
                </button>
                <button
                  onClick={save}
                  disabled={saving}
                  className="px-5 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-bold flex items-center gap-2 hover:opacity-90 disabled:opacity-50"
                  data-testid="save-perf-btn"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Save
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminPerformances;
